import React, { useState } from "react";

export type STItemForSO = {
  inventoryItemCode: string;
  itemName: string;
  requestedQty: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  items: STItemForSO[];
  onCreate: (
    prices: { inventoryItemCode: string; salePrice: number }[],
  ) => void;
};

const ConvertToSOPriceModal: React.FC<Props> = ({
  open,
  onClose,
  items,
  onCreate,
}) => {
  const [priceMap, setPriceMap] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleChange = (code: string, value: string) => {
    setPriceMap((prev) => ({ ...prev, [code]: value }));
  };

  const handleCreate = () => {
    setError(null);

    const result: { inventoryItemCode: string; salePrice: number }[] = [];

    for (const item of items) {
      const raw = priceMap[item.inventoryItemCode];
      const price = Number(raw);

      if (!raw || isNaN(price) || price <= 0) {
        setError(`Enter valid sale price for ${item.itemName}`);
        return;
      }

      result.push({
        inventoryItemCode: item.inventoryItemCode,
        salePrice: price,
      });
    }

    onCreate(result);
  };

  return (
    <div
      id="warehouse-stock-transfer-convert-to-so-price-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4 "
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh] wh-modal-xs">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">Convert to Sales Order</h2>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {items.map((item) => (
            <div
              key={item.inventoryItemCode}
              id={`warehouse-stock-transfer-convert-to-so-price-modal-item-row-${item.inventoryItemCode}`}
              className="border rounded-xl p-4 flex flex-col gap-2"
            >
              <div className="flex justify-between">
                <div>
                  <p className="font-medium">{item.itemName}</p>
                  <p className="text-sm text-gray-500">
                    {item.inventoryItemCode}
                  </p>
                </div>
              </div>

              <input
                id={`warehouse-stock-transfer-convert-to-so-price-modal-item-row-${item.inventoryItemCode}-sale-price`}
                type="number"
                placeholder="Enter sale price (₹) including GST"
                value={priceMap[item.inventoryItemCode] ?? ""}
                onChange={(e) =>
                  handleChange(item.inventoryItemCode, e.target.value)
                }
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ))}

          {error && <p className="text-sm text-red-600 font-medium">{error}</p>}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-end gap-3">
          <button
            id="warehouse-stock-transfer-convert-to-so-price-modal-cancel-btn"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            id="warehouse-stock-transfer-convert-to-so-price-modal-submit-btn"
            onClick={handleCreate}
            className="px-5 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
          >
            Create SO
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConvertToSOPriceModal;
